import { Component, EventEmitter, Input, OnInit, Output, ViewChild, inject } from '@angular/core';
import { TableModule } from 'primeng/table';
import { EmptyStateComponent, EnterpriseDto, GroupSearchService, PaginationComponent } from 'lib';
import { CommonModule } from '@angular/common';
import { LazyLoadEvent } from 'primeng/api';
import { RouterLink } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faShieldHalved } from '@fortawesome/free-solid-svg-icons';

export interface GroupSelectionEvent {
    selectedIds: Set<number>;
}

@Component({
    selector: 'mmoga-groups-table',
    standalone: true,
    imports: [
        EmptyStateComponent, TableModule, CommonModule, PaginationComponent, RouterLink, FaIconComponent
    ],
    templateUrl: './groups-table.component.html',
    styleUrl: './groups-table.component.scss'
})
export class GroupsTableComponent implements OnInit {
    private groupSearchService = inject(GroupSearchService);

    tableData!: EnterpriseDto[];
    currentPage = 1;
    itemsPerPage = 10;
    totalItems = 0;
    searchTerm = '';
    sortBy = 'name';
    sortOrder = 'ASC';
    isLoading = false;

    selectedIds = new Set<number>();
    readonly faShield = faShieldHalved;

    @Input() isPasswordResetDisabled = false;
    @Output() selectionChange = new EventEmitter<GroupSelectionEvent>();
    @Output() passwordResetRequested = new EventEmitter<number[]>();
    @ViewChild('pagination') pagination!: PaginationComponent;

    ngOnInit(): void {
        this.goToFirstPage();
    }

    loadData(): void {
        this.isLoading = true;
        if (this.searchTerm === null) {
            this.searchTerm = '';
        }
        this.groupSearchService.getGroups(this.searchTerm, this.currentPage - 1, this.itemsPerPage, this.sortBy, this.sortOrder)
            .subscribe({
                next: (data) => {
                    this.tableData = data.content;
                    this.totalItems = data.totalElements;
                    this.pagination = new PaginationComponent();
                    this.pagination.totalItems = this.totalItems;
                    if (data.content.length === 0 && this.currentPage > 1) {
                        this.goToFirstPage();
                    }
                    this.pagination?.ngOnInit();
                    this.isLoading = false;
                }
            });
    }

    goToFirstPage() {
        this.currentPage = 1;
        this.loadData();
    }

    customSort(event: LazyLoadEvent): void {
        this.sortBy = event.sortField ? event.sortField : 'name';
        this.sortOrder = event.sortOrder === 1 ? 'ASC' : 'DESC';
        this.loadData();
    }

    onSelectItemsPerPage(itemsPerPage: number): void {
        this.itemsPerPage = itemsPerPage;
    }

    onChangeCurrentPage(currentPage: number): void {
        this.currentPage = currentPage;
        this.loadData();
    }

    isCurrentPageAllSelected(): boolean {
        return this.tableData?.length > 0 && this.tableData.every(item => this.selectedIds.has(item.id!));
    }

    get allPagesSelected(): boolean {
        return this.selectedIds.size >= this.totalItems && this.totalItems > 0;
    }

    onPasswordResetClick(): void {
        if (this.isPasswordResetDisabled) {
            return;
        }

        this.passwordResetRequested.emit(Array.from(this.selectedIds));
    }

    toggleCurrentPage(checked: boolean): void {
        this.tableData?.forEach(item => {
            if (checked) {
                this.selectedIds.add(item.id!);
            } else {
                this.selectedIds.delete(item.id!);
            }
        });
        this.emitSelection();
    }

    toggleRow(item: EnterpriseDto, checked: boolean): void {
        if (checked) {
            this.selectedIds.add(item.id!);
        } else {
            this.selectedIds.delete(item.id!);
        }
        this.emitSelection();
    }

    selectAllPages(): void {
        this.groupSearchService.getAllGroupIds(this.searchTerm, this.totalItems, this.sortBy, this.sortOrder)
            .subscribe(ids => {
                ids.forEach(id => this.selectedIds.add(id));
                this.emitSelection();
            });
    }

    clearSelection(): void {
        this.selectedIds.clear();
        this.emitSelection();
    }

    private emitSelection(): void {
        this.selectionChange.emit({ selectedIds: this.selectedIds });
    }
}
